import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  IconButton,
  Chip,
  TablePagination,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Stack,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  Search,
  Person,
  Phone,
  WhatsApp,
  LocationOn,
  Email,
  PersonAdd,
  Edit,
  Block,
  CheckCircle,
  Work,
  Delete,
  Download,
  Refresh,
  AddCircle,
  History,
  CreditCard,
  Home,
  AccountBalance,
  CloudUpload,
} from '@mui/icons-material';
import DataStateHandler from '../../components/Loader/DataStateHandler';
import { customerService } from '../../services/customer';
import {
  readExcelFile,
  exportAsExcel,
  exportAsCSV,
  exportAsPDF,
  downloadExcelFile,
} from '../../utils/importExport';
import { viewUrl } from '../../services/s3';
import { hasWritePermission } from '../../utils/menuFilter';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const mapCustomer = (item) => {
  const basicRaw = item?.basicDetails;
  const personalRaw = item?.personalDetails;
  const otherRaw = item?.otherDetails;

  const basic =
    basicRaw && typeof basicRaw === 'object' && !Array.isArray(basicRaw)
      ? basicRaw
      : (Array.isArray(basicRaw) ? basicRaw[0] : undefined) || {};
  const personal =
    personalRaw && typeof personalRaw === 'object' && !Array.isArray(personalRaw)
      ? personalRaw
      : (Array.isArray(personalRaw) ? personalRaw[0] : undefined) || {};
  const other =
    otherRaw && typeof otherRaw === 'object' && !Array.isArray(otherRaw)
      ? otherRaw
      : (Array.isArray(otherRaw) ? otherRaw[0] : undefined) || {};

  const budgetValue = item?.budget;
  const budgetLabel =
    typeof budgetValue === 'number'
      ? `₹${budgetValue.toLocaleString()}`
      : budgetValue ?? '-';

  const totalSpent = typeof item?.totalSpent === 'number'
    ? item.totalSpent
    : Array.isArray(item?.purchaseHistory)
      ? item.purchaseHistory.reduce(
        (sum, entry) => sum + (typeof entry?.amount === 'number' ? entry.amount : 0),
        0,
      )
      : 0;
  const totalPurchases = typeof item?.totalPurchases === 'number'
    ? item.totalPurchases
    : Array.isArray(item?.purchaseHistory)
      ? item.purchaseHistory.length
      : Number.isFinite(item?.customerNo)
        ? item.customerNo
        : 0;

  const id = item?._id ?? '';
  const fallbackId = item?.customerNo
    ? `CUST${String(item.customerNo).padStart(4, '0')}`
    : id
      ? `CUST${id.slice(-6).toUpperCase()}`
      : 'CUST-NA';

  return {
    id,
    customerId: item?.customerId || fallbackId,
    name: item?.name ?? '-',
    fatherName: basic?.fatherName ?? basic?.father ?? '-',
    profession: item.profession ?? '-',
    mobile: item?.phone ?? '-',
    whatsappNumber: item?.whatsapp ?? '-',
    email: item?.email ?? '-',
    city: item?.city ?? '-',
    state: item?.state ?? '-',
    pinCode: item?.pinCode ?? '',
    address: other?.address ?? other?.createdDetail ?? '-',
    dateOfBirth: personal?.dob ?? '-',
    age: personal?.age ?? '',
    anniversary: personal?.anniversary ?? '-',
    maritalStatus: personal?.maritalStatus ?? basic?.maritalStatus ?? item?.maritalStatus ?? '-',
    gender: basic?.gender ?? '-',
    bloodGroup: basic?.bloodGroup ?? '-',
    joinDate: item?.joinDate ?? '-',
    createdDate: item?.createdAt ? new Date(item.createdAt).toLocaleString() : '-',
    createdBy: item?.createdBy ?? '-',
    associateName: item?.associateName ?? '-',
    assignedTo: item?.assignedTo ?? null,
    status: (item?.status === false || item?.status === 'inactive' || item?.isDeleted) ? 'Inactive' : 'Active',
    totalPurchases,
    totalSpent,
    aadahr: personal?.aadahr ?? personal?.aadhar ?? '-',
    panNumber: personal?.pan ?? '-',
    occupation: item?.profession ?? '-',
    companyName: item?.companyName ?? '-',
    annualIncome: item?.annualIncome ?? '-',
    emergencyContact: other?.emergencyContact ?? other?.salaryRange ?? '-',
    emergencyContactName: other?.emergencyContactName ?? other?.createdDetail ?? '-',
    relation: other?.relation ?? basic?.relation ?? '-',
    propertyInterest: item?.interestedIn ?? other?.createdDetail ?? '-',
    budget: budgetLabel,
    preferredLocation: other?.preferredLocation ?? '-',
    remark: other?.remark ?? item?.notes ?? '-',
    salaryRange: other?.salaryRange ?? '',
    source: other?.createdDetail ?? '',
    image:
      item?.image ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(item?.name ?? 'Customer')}&background=536DFE&color=fff`,
    imgUrl: item?.imgUrl || item?.image || '',
    purchaseHistory: Array.isArray(item?.purchaseHistory) ? item.purchaseHistory : [],
    // Include raw item data for edit form
    _id: item?._id,
    _raw: item, // Store raw API data for edit form
  };
};

// Reset Button Component
const ResetButton = ({ onClick, disabled }) => (
  <Button
    variant="outlined"
    color="error"
    startIcon={<Refresh />}
    onClick={onClick}
    disabled={disabled}
    fullWidth
    sx={{
      height: '56px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '12px',
      borderRadius: 2,
      borderColor: '#d32f2f',
      '&:hover': {
        borderColor: '#b71c1c',
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
      },
    }}
  >
    Reset
  </Button>
);

const Customer = () => {
  const history = useHistory();
  const canWriteCustomer = hasWritePermission('members.customer-details');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };
  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [selectedTab, setSelectedTab] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cardTabValue, setCardTabValue] = useState({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  const resetImportState = () => {
    setImportFileName('');
    setSelectedImportFile(null);
    setImportError('');
    setImportSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [customerImages, setCustomerImages] = useState({}); // Store signed URLs for customer images
  const [statusAlert, setStatusAlert] = useState({ open: false, message: '', severity: 'success' });
  const [statusConfirmDialog, setStatusConfirmDialog] = useState({
    open: false,
    customer: null,
    newStatus: null
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    customer: null
  });

  // Auto-close alert after 5 seconds
  useEffect(() => {
    if (statusAlert.open) {
      const timer = setTimeout(() => {
        setStatusAlert(prev => ({ ...prev, open: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [statusAlert.open]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    activeCount: 0,
    inactiveCount: 0,
  });
  const [activeFilter, setActiveFilter] = useState(null); // null = all, true = active, false = inactive
  const [userRole, setUserRole] = useState(null);
  const [userDesignation, setUserDesignation] = useState(null);

  useEffect(() => {
    const fetchUserPermissions = async () => {
      // First try to get from localStorage
      const storedUser = localStorage.getItem('user');
      let role = null;
      let designation = null;

      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          role = parsedUser?.role || null;
          designation = parsedUser?.designation || null;
        } catch (err) {
          console.error('Failed to parse user from localStorage', err);
        }
      }

      // If designation is missing or we want to be sure, fetch from API
      // Especially if role is 'admin' or similar where permissions matter
      try {
        // We can't easily import getProfileDetail here due to potential circular deps or structure
        // But we can try to use it if we import it.
        // Let's assume we can rely on localStorage for role but maybe fetch profile for designation if missing?
        // Or just always fetch profile to be safe.
        const token = localStorage.getItem('token');
        if (token) {
          const { getProfileDetail } = require('../../services/profile');
          const profileData = await getProfileDetail();
          if (profileData && profileData.status && profileData.data) {
            const apiDesignation = profileData.data.designation;
            const apiRole = profileData.data.role; // Note: API returns 'Admin' (capitalized) or 'associate'

            if (apiDesignation) designation = apiDesignation;
            if (apiRole) role = apiRole; // Update role from API too
          }
        }
      } catch (error) {
        console.error('Failed to fetch user profile for permissions', error);
      }

      setUserRole(role);
      setUserDesignation(designation);
    };

    fetchUserPermissions();
  }, []);

  const fetchCustomers = useCallback(async (pageNum = 1, active = null, limit = 10, search = '') => {
    setLoadingCustomers(true);
    setCustomersError('');
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(limit),
      });

      // Add active filter if specified
      if (active !== null) {
        params.append('active', String(active));
      }

      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await fetch(`${base}/api/customers?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch customers');
      }

      const data = Array.isArray(result?.data) ? result.data : [];
      const list = data.map(mapCustomer);
      setCustomers(list);

      // Update pagination info from extra field
      if (result?.extra) {
        const newLimit = parseInt(result.extra.limit || '20', 10);
        setPagination(prev => ({
          ...prev,
          page: parseInt(result.extra.page || '1', 10),
          limit: newLimit,
          total: parseInt(result.extra.total || '0', 10),
          activeCount: parseInt(result.extra.activeCount || '0', 10),
          inactiveCount: parseInt(result.extra.inactiveCount || '0', 10),
        }));
      }
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      setCustomersError(err.message || 'Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  useEffect(() => {
    const activeValue = tabValue === 0 ? null : tabValue === 1 ? true : false;
    setActiveFilter(activeValue);

    // This is the single source of truth for loading customers
    fetchCustomers(pagination.page, activeValue, rowsPerPage, searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, rowsPerPage, pagination.page, searchQuery]);

  // Fetch signed URLs for customer images
  useEffect(() => {
    const loadCustomerImages = async () => {
      if (customers.length === 0) return;

      const imagePromises = customers.map(async (customer) => {
        const imgUrl = customer.imgUrl || customer.image;
        if (!imgUrl || imgUrl.trim() === '') {
          return { customerId: customer.customerId || customer.id, imageUrl: null };
        }

        try {
          const signedUrl = await viewUrl(imgUrl);
          return {
            customerId: customer.customerId || customer.id,
            imageUrl: signedUrl || imgUrl, // Fallback to original if viewUrl returns null
          };
        } catch (error) {
          console.error(`Failed to load image for customer ${customer.customerId}:`, error);
          // If it's already a full URL, use it; otherwise return null
          return {
            customerId: customer.customerId || customer.id,
            imageUrl: (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) ? imgUrl : null,
          };
        }
      });

      const imageResults = await Promise.all(imagePromises);
      const imageMap = {};
      imageResults.forEach(({ customerId, imageUrl }) => {
        if (customerId) {
          imageMap[customerId] = imageUrl;
        }
      });
      setCustomerImages(imageMap);
    };

    loadCustomerImages();
  }, [customers]);

  // Handle functions
  const handleView = (customer) => {
    setSelectedCustomer(customer);
    // Reset tab to first tab when opening dialog
    setCardTabValue(prev => ({ ...prev, [customer.customerId + '_detail']: 0 }));
    setDetailsDialogOpen(true);
  };

  const handleEdit = (customer) => {
    // Prefer raw API data (has imgUrl and all original fields), fallback to mapped customer
    const customerToEdit = customer?._raw || customer;
    history.push({
      pathname: '/app/members/customer/edit',
      state: { customer: customerToEdit }
    });
  };

  const handleBlock = (customer) => {
    if (window.confirm(`Are you sure you want to ${customer.status === 'Active' ? 'block' : 'unblock'} ${customer.name}?`)) {
      showAlert(`Customer ${customer.customerId} ${customer.status === 'Active' ? 'blocked' : 'unblocked'}`);
    }
  };

  const handleStatusToggle = (customer) => {
    const newStatus = customer.status === 'Active' ? 'Inactive' : 'Active';
    setStatusConfirmDialog({
      open: true,
      customer,
      newStatus
    });
  };

  const handleStatusConfirm = async () => {
    const { customer, newStatus } = statusConfirmDialog;
    if (!customer || !newStatus) return;

    try {
      // Send only explicit strings for status: "active" or "inactive"
      const updatePayload = {
        status: newStatus.toLowerCase(),
      };
      await customerService.updateCustomer(customer.id || customer._id, updatePayload);

      // Update local state and trigger refresh to ensure lists correctly show the changed status
      await fetchCustomers(pagination.page, activeFilter, pagination.limit, searchQuery);

      setStatusAlert({
        open: true,
        message: `Customer ${customer.name} status updated to ${newStatus}`,
        severity: 'success'
      });
    } catch (error) {
      setStatusAlert({
        open: true,
        message: `Failed to update customer status: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setStatusConfirmDialog({ open: false, customer: null, newStatus: null });
    }
  };

  const handleStatusCancel = () => {
    setStatusConfirmDialog({ open: false, customer: null, newStatus: null });
  };

  const handleAddLead = (customer) => {
    history.push({
      pathname: '/app/members/customer/add-lead',
      state: { customer: customer }
    });
  };

  const handleViewHistory = (customer) => {
    history.push('/app/members/customer/history', { customer });
  };

  const handleDelete = (customer) => {
    setDeleteConfirmDialog({
      open: true,
      customer
    });
  };

  const handleDeleteConfirm = async () => {
    const { customer } = deleteConfirmDialog;
    if (!customer) return;
    const customerId = customer?.id || customer?._id;
    if (!customerId) return;

    try {
      setDeletingCustomerId(customerId);
      await customerService.deleteCustomer(customerId);
      // Refresh with current filter and page
      const activeValue = tabValue === 0 ? null : tabValue === 1 ? true : false;
      fetchCustomers(pagination.page, activeValue, pagination.limit, searchQuery);
      setDeleteConfirmDialog({ open: false, customer: null });
    } catch (error) {
      console.error('Failed to delete customer:', error);
      showAlert(error.message || 'Failed to delete customer.', 'Error');
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ open: false, customer: null });
  };

  const handleChangePage = (_, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPagination(prev => ({ ...prev, page: 1, limit: newRowsPerPage }));
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    setIsSuggestionOpen(true);
  };

  const handleSearchSubmit = (value = null) => {
    const rawValue = value ?? searchInput;
    const nextValue = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const trimmedValue = nextValue.trim();

    setSearchQuery(trimmedValue);
    setSearchInput(nextValue);
    setIsSuggestionOpen(false);

    // Reset to page 1 on search
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleReset = useCallback(() => {
    setSearchInput('');
    setSearchQuery('');
    setTabValue(0);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleRefresh = useCallback(() => {
    const activeValue = tabValue === 0 ? null : tabValue === 1 ? true : false;
    fetchCustomers(pagination.page, activeValue, rowsPerPage, searchQuery);
  }, [fetchCustomers, pagination.page, tabValue, rowsPerPage, searchQuery]);

  const handleTabChange = (tabId, newValue) => {
    if (selectedTab?.id === tabId && selectedTab?.value === newValue) {
      setSelectedTab(null);
    } else {
      setSelectedTab({ id: tabId, value: newValue });
    }
  };

  const suggestionOptions = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    const list = Array.isArray(customers) ? customers : [];

    if (!query) {
      return list.slice(0, 5);
    }

    return list
      .filter((customer) => {
        const name = (customer.name || '').toLowerCase();
        const mobile = String(customer.mobile || '');
        const id = (customer.customerId || '').toLowerCase();
        const email = (customer.email || '').toLowerCase();
        return (
          name.includes(query) ||
          mobile.includes(query) ||
          id.includes(query) ||
          email.includes(query)
        );
      })
      .slice(0, 5);
  }, [customers, searchInput]);

  const handleSuggestionSelect = (customer) => {
    const fallbackValue = customer?.mobile || customer?.name || customer?.customerId || '';
    const value = typeof fallbackValue === 'string' ? fallbackValue : String(fallbackValue);
    setSearchInput(value);
    handleSearchSubmit(value);
  };

  const getSelectedTab = (tabId) => {
    if (selectedTab === null) {
      return 0;
    }
    return selectedTab?.id === tabId ? selectedTab.value : 0;
  };

  const handleCardTabChange = (customerId, newValue) => {
    setCardTabValue((prev) => ({ ...prev, [customerId]: newValue }));
  };

  const getCardTabValue = (customerId) => {
    return cardTabValue[customerId] || 0;
  };

  const handleDownloadSample = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            customerId: 'CUST100',
            name: 'Sample Customer',
            fatherName: 'Sample Father',
            profession: 'Business',
            mobile: '9000000000',
            whatsappNumber: '9000000000',
            email: 'sample@example.com',

            state: 'Sample State',
            city: 'Sample City',
            address: '123 Sample Address',
            dateOfBirth: '1990-01-01',
            anniversary: '2015-01-01',
            gender: 'Male',
            bloodGroup: 'O+',
            joinDate: '2025-01-01',
            createdDate: '2025-01-01 10:00 AM',
            createdBy: 'Admin',
            status: 'Active',
            totalPurchases: 3,
            totalSpent: 1500000,
            aadharNumber: '1111-2222-3333',
            panNumber: 'ABCDE1234F',
            occupation: 'Entrepreneur',
            companyName: 'Sample Pvt Ltd',
            annualIncome: '20 LPA',
            emergencyContact: '9100000000',
            emergencyContactName: 'Sample Contact',
            relation: 'Spouse',
            propertyInterest: 'Residential',
            budget: '50-75 Lakhs',
            preferredLocation: 'Sample Location',
            remark: 'Sample remark',
          },
        ],
        sheetName: 'Sample',
        fileName: 'customer_import_sample.xlsx',
      });
    } catch (error) {
      setImportError(error.message || 'Failed to download sample file.');
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            customerId: '',
            name: '',
            fatherName: '',
            professionalName: '',
            mobile: '',
            whatsappNumber: '',
            email: '',

            state: '',
            city: '',
            address: '',
            dateOfBirth: '',
            anniversary: '',
            gender: '',
            bloodGroup: '',
            joinDate: '',
            createdDate: '',
            createdBy: '',
            status: '',
            totalPurchases: '',
            totalSpent: '',
            aadharNumber: '',
            panNumber: '',
            occupation: '',
            companyName: '',
            annualIncome: '',
            emergencyContact: '',
            emergencyContactName: '',
            relation: '',
            propertyInterest: '',
            budget: '',
            preferredLocation: '',
            remark: '',
          },
        ],
        sheetName: 'Template',
        fileName: 'customer_import_template.xlsx',
      });
    } catch (error) {
      setImportError(error.message || 'Failed to download template file.');
    }
  };

  const handleImportClick = () => {
    resetImportState();
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    resetImportState();
    setImportDialogOpen(false);
  };

  const handleChooseImportFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setSelectedImportFile(file);
      setImportFileName(file.name);
    }
  };

  const handlePerformImport = async () => {
    if (!selectedImportFile) return;
    try {
      const rows = await readExcelFile(selectedImportFile);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error('Excel sheet is empty or invalid.');
      }
      const timestamp = Date.now();
      const normalized = rows
        .map((row, index) => {
          const customerId =
            row.customerId ||
            row.CustomerId ||
            row['Customer ID'] ||
            `CUST-IMP-${timestamp}-${index + 1}`;
          const name = row.name || row.Name || row.fullName || row.FullName || '';
          if (!name) return null;

          const profession = row.profession || row.Profession || row.occupation || row.Occupation || '';
          const status = row.status || row.Status || 'Active';
          const state = row.state || row.State || '';
          const city = row.city || row.City || '';

          return {
            id: index + 1,
            customerId,
            name,
            fatherName: row.fatherName || row.FatherName || '',
            profession,
            mobile: String(row.mobile || row.Mobile || ''),
            whatsappNumber: String(row.whatsappNumber || row.WhatsappNumber || row.WhatsApp || ''),
            email: row.email || row.Email || '',
            state,
            city,
            address: row.address || row.Address || '',
            dateOfBirth: row.dateOfBirth || row.DateOfBirth || '',
            anniversary: row.anniversary || row.Anniversary || '',
            gender: row.gender || row.Gender || '',
            bloodGroup: row.bloodGroup || row.BloodGroup || '',
            joinDate: row.joinDate || row.JoinDate || '',
            createdDate: row.createdDate || row.CreatedDate || '',
            createdBy: row.createdBy || row.CreatedBy || '',
            status,
            totalPurchases: Number(row.totalPurchases || row.TotalPurchases || 0),
            totalSpent: Number(row.totalSpent || row.TotalSpent || 0),
            aadharNumber: row.aadharNumber || row.AadharNumber || '',
            panNumber: row.panNumber || row.PanNumber || '',
            occupation: row.occupation || row.Occupation || profession,
            companyName: row.companyName || row.CompanyName || '',
            annualIncome: row.annualIncome || row.AnnualIncome || '',
            emergencyContact: row.emergencyContact || row.EmergencyContact || '',
            emergencyContactName: row.emergencyContactName || row.EmergencyContactName || '',
            relation: row.relation || row.Relation || '',
            propertyInterest: row.propertyInterest || row.PropertyInterest || '',
            budget: row.budget || row.Budget || '',
            preferredLocation: row.preferredLocation || row.PreferredLocation || '',
            remark: row.remark || row.Remark || '',
            image: row.image || row.Image || 'https://i.pravatar.cc/150?img=1',
            purchaseHistory: [],
          };
        })
        .filter(Boolean);

      if (!normalized.length) {
        throw new Error('No valid customer records were found in the file.');
      }

      setCustomers(normalized);
      setImportSuccess(`Imported ${normalized.length} customer(s) successfully.`);
      setImportError('');
      setSearchQuery('');
      setSearchInput('');
      setTabValue(0);
      // Refresh the list after import
      fetchCustomers(1, null, rowsPerPage, '');
    } catch (error) {
      setImportError(error.message || 'Failed to import customers. Please check the file format.');
      setImportSuccess('');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenExportMenu = (event) => setExportMenuAnchorEl(event.currentTarget);
  const handleCloseExportMenu = () => setExportMenuAnchorEl(null);

  const buildExportRows = (list) => {
    return list.map((item) => {
      return {
        'Customer ID': item.customerId || '-',
        'Full Name': item.name || '-',
        'Father Name': item.fatherName || '-',
        'Mobile': item.mobile || '-',
        'WhatsApp': item.whatsappNumber || '-',
        'Email': item.email || '-',
        'Gender': item.gender || '-',
        'DOB': item.dateOfBirth || '-',
        'Marital Status': item.maritalStatus || '-',
        'Profession': item.profession || '-',
        'Aadhar': item.aadahr || item.aadharNumber || item.aadhar || '-',
        'PAN': item.panNumber || item.pan || '-',
        'City': item.city || '-',
        'State': item.state || '-',
        'Address': item.address || '-',
        'Join Date': item.joinDate || '-',
        'Status': item.status || '-',
        'Created By': item.createdBy || '-',
        'Created Date': item.createdDate || '-',
      };
    });
  };

  const handleExportData = (format) => {
    // Export data currently shown on the page (matching the current page limit)
    const list = Array.isArray(customers) ? customers : [];

    if (!list.length) {
      showAlert('No customer data available on the current page to export.');
      return;
    }

    const rows = buildExportRows(list);
    const title = 'Customer List';
    const fileNameBase = 'Customers_Export';

    if (format === 'pdf') {
      exportAsPDF({ rows, title, fileNameBase });
    } else if (format === 'csv') {
      exportAsCSV({ rows, fileNameBase });
    } else {
      exportAsExcel({ rows, sheetName: 'Customers', fileNameBase });
    }
  };

  const handleExportPdf = () => handleExportData('pdf');
  const handleExportCsv = () => handleExportData('csv');
  const handleExportExcel = () => handleExportData('excel');

  // Server-side search is now handled by the API, so display customers directly
  // (Search filtering is done on the server side via the 'search' parameter)
  const displayData = customers;
  // Use counts from API response instead of calculating from current page
  const activeCount = pagination.activeCount;
  const inactiveCount = pagination.inactiveCount;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Status Alert */}
      {statusAlert.open && (
        <Alert
          severity={statusAlert.severity}
          onClose={() => setStatusAlert({ ...statusAlert, open: false })}
          sx={{ mb: 2 }}
        >
          {statusAlert.message}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Customer Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all customers
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {(userDesignation?.toLowerCase() === 'owner' || canWriteCustomer) && (
              <ExportButton
                onExportPdf={handleExportPdf}
                onExportCsv={handleExportCsv}
                onExportExcel={handleExportExcel}
                variant="outlined"
                sx={{
                  px: 2.5,
                  py: 1,
                  fontSize: '14px'
                }}
              />
            )}
            <Box display="flex" gap={2}>
              {(userDesignation?.toLowerCase() === 'owner' || canWriteCustomer) && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CloudUpload />}
                  onClick={() => history.push('/app/members/customer/bulk-import')}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '15px',
                    borderRadius: 2
                  }}
                >
                  Bulk Import
                </Button>
              )}


              {canWriteCustomer && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAdd />}
                  onClick={() => history.push('/app/members/customer/add')}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    borderRadius: 2
                  }}
                >
                  Add Customer
                </Button>
              )}
            </Box>
            <RefreshButton
              onClick={handleRefresh}
              loading={loadingCustomers}
              variant="outlined"
              size="medium"
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1.2,
                fontSize: '15px',
                borderRadius: 2,
                height: '48px',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Search Bar */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'visible' }}>
        <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={10} md={10.5}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  placeholder="Search by customer ID, name, mobile, email..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                  onFocus={() => {
                    setIsSuggestionOpen(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setIsSuggestionOpen(false), 120);
                  }}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
                {isSuggestionOpen && suggestionOptions.length > 0 && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 1,
                      zIndex: 10,
                      borderRadius: 2,
                      maxHeight: 240,
                      overflowY: 'auto'
                    }}
                  >
                    {suggestionOptions.map((customer) => (
                      <Box
                        key={customer.customerId}
                        sx={{
                          px: 2,
                          py: 1.2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(83, 109, 254, 0.08)'
                          }
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSuggestionSelect(customer)}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {customer.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {customer.customerId}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {customer.mobile}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
              <ResetButton onClick={handleReset} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filter Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              minHeight: 56
            }
          }}
        >
          <Tab label={`All Customers (${pagination.total})`} />
          <Tab label={`Active (${activeCount})`} />
          <Tab label={`Inactive (${inactiveCount})`} />
        </Tabs>
      </Box>

      {/* Customer Cards Grid */}
      <DataStateHandler
        loading={loadingCustomers}
        error={customersError}
        onRetry={() => {
          const activeValue = tabValue === 0 ? null : tabValue === 1 ? true : false;
          fetchCustomers(pagination.page, activeValue, rowsPerPage, searchQuery);
        }}
        isEmpty={!loadingCustomers && !customersError && displayData.length === 0}
        emptyTitle="No customers found"
        emptyMessage="Try adjusting your search or filters, or add a new customer."
        renderLoading={() => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        renderEmpty={() => (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              No matching customers
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Try different keywords or reset the filters to see all customers.
            </Typography>
            {canWriteCustomer && (
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => history.push('/app/members/customer/add')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Add New Customer
              </Button>
            )}
          </Box>
        )}
      >
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {displayData.map((customer) => {
              const activeTab = getSelectedTab(customer.id);

              return (
                <Grid item xs={12} key={customer.customerId}>
                  <Card
                    sx={{
                      boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Card Header - Blue with 3 columns */}
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: '#536DFE',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                          Customer ID:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
                          {customer.customerId}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                          Total Spent:
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CreditCard sx={{ fontSize: 16 }} />
                          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
                            ₹{(customer.totalSpent ?? 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                          Status:
                        </Typography>
                        {canWriteCustomer ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleStatusToggle(customer)}
                            sx={{
                              backgroundColor: customer.status === 'Active' ? '#4caf50' : '#f44336',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '12px',
                              height: '24px',
                              minWidth: '60px',
                              borderRadius: 3,
                              '&:hover': {
                                backgroundColor: customer.status === 'Active' ? '#45a049' : '#d32f2f',
                              }
                            }}
                          >
                            {customer.status}
                          </Button>
                        ) : (
                          <Chip
                            label={customer.status}
                            size="small"
                            sx={{
                              backgroundColor: customer.status === 'Active' ? '#4caf50' : '#f44336',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '12px',
                              height: '24px',
                              minWidth: '60px',
                              borderRadius: 3,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Card Content */}
                    <CardContent sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {/* Left Side: Customer Info */}
                        <Grid item xs={12} md={5}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            {/* Avatar + Badge */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={customer.profession}
                                sx={{
                                  backgroundColor: customer.profession && customer.profession.trim() !== '' ? '#FFC107' : 'transparent',
                                  color: customer.profession && customer.profession.trim() !== '' ? '#000' : 'transparent',
                                  fontWeight: 700,
                                  fontSize: '11px',
                                  height: '22px',
                                  border: customer.profession && customer.profession.trim() !== '' ? 'none' : '1px solid transparent',
                                }}
                              />
                              <Avatar
                                src={
                                  customerImages[customer.customerId] ||
                                  customer.imgUrl ||
                                  customer.image ||
                                  `https://ui-avatars.com/api/?name=${encodeURIComponent(customer.name || 'Customer')}&background=536DFE&color=fff`
                                }
                                alt={customer.name}
                                sx={{
                                  width: 100,
                                  height: 100,
                                  backgroundColor: '#E3F2FD',
                                  border: '3px solid #2196F3',
                                }}
                                onError={(e) => {
                                  // If image fails to load, show fallback avatar
                                  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(customer.name || 'Customer')}&background=536DFE&color=fff`;
                                  if (e.target.src !== fallbackUrl) {
                                    e.target.src = fallbackUrl;
                                  } else {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              >
                                {customer.name ? customer.name.charAt(0).toUpperCase() : <Person sx={{ fontSize: 40, color: '#2196F3' }} />}
                              </Avatar>
                            </Box>

                            {/* Customer Details */}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {customer.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {customer.profession}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {customer.state},{customer.city}
                              </Typography>
                              <Chip
                                label={customer.status}
                                size="small"
                                sx={{
                                  backgroundColor: customer.status === 'Active' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  height: '22px',
                                  mb: 1
                                }}
                              />

                              {/* Contact Info */}
                              <Box sx={{ mt: 1.5 }}>
                                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                                  <Email sx={{ fontSize: 16, color: '#666' }} />
                                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                    {customer.email}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                                  <Phone sx={{ fontSize: 16, color: '#25D366' }} />
                                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                    {customer.mobile}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                    {customer.state} ,{customer.city}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                                  <Work sx={{ fontSize: 16, color: '#666' }} />
                                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                    Associate: {customer.associateName}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>

                        {/* Right Side: Tabs and Tab Content */}
                        <Grid item xs={12} md={7}>
                          <Box>
                            <Tabs
                              value={activeTab}
                              onChange={(e, newValue) => handleTabChange(customer.id, newValue)}
                              variant="scrollable"
                              scrollButtons="auto"
                              allowScrollButtonsMobile
                              sx={{
                                minHeight: 40,
                                borderBottom: '1px solid #e0e0e0',
                                '& .MuiTab-root': {
                                  minHeight: 40,
                                  textTransform: 'none',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                }
                              }}
                            >
                              <Tab label="Basic Details" />
                              <Tab label="Personal Details" />
                              <Tab label="Other Details" />
                            </Tabs>

                            {/* Tab Content */}
                            <Box sx={{ mt: 2 }}>
                              {/* Basic Details */}
                              {activeTab === 0 && (
                                <Box>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Gender
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.gender}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Father's Name
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.fatherName}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    {/* <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                      Blood Group
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                      {customer.bloodGroup}
                                    </Typography>
                                  </Box>
                                </Grid> */}
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Profession
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.profession}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Marital Status
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.maritalStatus || 'Single'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              )}

                              {/* Personal Details */}
                              {activeTab === 1 && (
                                <Box>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Date of Birth
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.dateOfBirth}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    {(customer.maritalStatus?.toLowerCase() === 'married' || customer.maritalStatus?.toLowerCase() === 'marriage') &&
                                      customer.anniversary && customer.anniversary !== '-' && customer.anniversary.trim() !== '' && (
                                        <Grid item xs={12} sm={6}>
                                          <Box sx={{ p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                              Anniversary
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                              {customer.anniversary}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          PAN Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.panNumber}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Aadhar Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.aadahr}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              )}

                              {/* Other Details */}
                              {activeTab === 2 && (
                                <Box>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Full Address
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.address}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Created Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.createdDate}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Created By
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.createdBy}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1, borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Remark
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {customer.remark}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              )}

                              {/* Purchase History removed from card view */}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Bottom Actions */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleView(customer)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px',
                            px: 2,
                          }}
                        >
                          View Details
                        </Button>
                        {canWriteCustomer && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => handleEdit(customer)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '12px',
                              px: 2,
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {canWriteCustomer && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAdd />}
                          onClick={() => handleAddLead(customer)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px',
                            px: 2,
                            // backgroundColor: 'primary.main',
                          }}
                        >
                          Add Lead
                        </Button>
                        )}
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<History />}
                          onClick={() => handleViewHistory(customer)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px',
                            px: 2,
                          }}
                        >
                          History
                        </Button>
                        {canWriteCustomer && (
                          <Tooltip title="Delete Customer">
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDelete(customer);
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', px: 2 }}
                                disabled={deletingCustomerId === (customer.id || customer._id)}
                                startIcon={
                                  deletingCustomerId === (customer.id || customer._id)
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <Delete fontSize="small" />
                                }
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {displayData.length > 0 && (
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <TablePagination
                
                component="div"
                count={pagination.total}
                rowsPerPage={pagination.limit}
                page={pagination.page - 1} // Convert 1-based API page to 0-based MUI page
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          )}
        </>
      </DataStateHandler>

      {/* Detailed View Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar
            src={
              selectedCustomer
                ? (customerImages[selectedCustomer.customerId] ||
                  selectedCustomer.imgUrl ||
                  selectedCustomer.image ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCustomer.name || 'Customer')}&background=536DFE&color=fff`)
                : null
            }
            alt={selectedCustomer?.name}
            sx={{
              width: 56,
              height: 56,
              border: '3px solid white'
            }}
            onError={(e) => {
              // If image fails to load, show fallback avatar
              if (selectedCustomer) {
                const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCustomer.name || 'Customer')}&background=536DFE&color=fff`;
                if (e.target.src !== fallbackUrl) {
                  e.target.src = fallbackUrl;
                } else {
                  e.target.style.display = 'none';
                }
              }
            }}
          >
            {selectedCustomer?.name ? selectedCustomer.name.charAt(0).toUpperCase() : <Person />}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedCustomer?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {selectedCustomer?.customerId} | {selectedCustomer?.profession}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedCustomer ? getCardTabValue(selectedCustomer.customerId + '_detail') : 0}
              onChange={(e, newValue) => selectedCustomer && handleCardTabChange(selectedCustomer.customerId + '_detail', newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px'
                }
              }}
            >
              <Tab label="Basic Details" />
              <Tab label="Personal Details" />
              <Tab label="Other Details" />
              <Tab label="Purchase History" />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {selectedCustomer && (
              <>
                {/* Basic Details Tab */}
                {getCardTabValue(selectedCustomer.customerId + '_detail') === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
                        Basic Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Father's Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.fatherName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Gender</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.gender}
                        </Typography>
                      </Box>
                    </Grid>
                    {/* <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Blood Group</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.bloodGroup}
                        </Typography>
                      </Box>
            </Grid> */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Profession</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.profession}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Marital Status</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.maritalStatus || 'Single'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Chip
                          label={selectedCustomer.status}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            backgroundColor: selectedCustomer.status === 'Active' ? '#4caf50' : '#f44336',
                            color: 'white'
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {/* Personal Details Tab */}
                {getCardTabValue(selectedCustomer.customerId + '_detail') === 1 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
                        Personal Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.dateOfBirth}
                        </Typography>
                      </Box>
                    </Grid>
                    {(selectedCustomer.maritalStatus?.toLowerCase() === 'married' || selectedCustomer.maritalStatus?.toLowerCase() === 'marriage') &&
                      selectedCustomer.anniversary && selectedCustomer.anniversary !== '-' && selectedCustomer.anniversary.trim() !== '' && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Anniversary</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {selectedCustomer.anniversary}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">PAN Number</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.panNumber}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Aadhar Number</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.aadahr}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {/* Other Details Tab */}
                {getCardTabValue(selectedCustomer.customerId + '_detail') === 2 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
                        Additional Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Created Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.createdDate}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Created By</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedCustomer.createdBy}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
                        <Typography variant="caption" color="text.secondary">Remark</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#856404', mt: 0.5 }}>
                          {selectedCustomer.remark}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {/* Purchase History Tab */}
                {getCardTabValue(selectedCustomer.customerId + '_detail') === 3 && (
                  <Grid container spacing={2}>
                    {Array.isArray(selectedCustomer.purchaseHistory) && selectedCustomer.purchaseHistory.length > 0 ? (
                      selectedCustomer.purchaseHistory.map((purchase) => (
                        <Grid item xs={12} key={purchase.id}>
                          <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2 }}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {purchase.project}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {purchase.unit}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Order ID: {purchase.id} · {new Date(purchase.date).toLocaleDateString()}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    ₹{purchase.amount.toLocaleString()}
                                  </Typography>
                                  <Chip
                                    label={purchase.paymentStatus}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      backgroundColor:
                                        purchase.paymentStatus === 'Completed'
                                          ? 'rgba(46, 125, 50, 0.12)'
                                          : purchase.paymentStatus === 'Pending'
                                            ? 'rgba(255, 193, 7, 0.12)'
                                            : 'rgba(25, 118, 210, 0.12)',
                                      color:
                                        purchase.paymentStatus === 'Completed'
                                          ? '#2e7d32'
                                          : purchase.paymentStatus === 'Pending'
                                            ? '#ff9800'
                                            : '#1976d2',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Payment Mode: {purchase.paymentMode}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          No purchase history available for this customer.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
          {canWriteCustomer && (
            <Button
              onClick={() => handleEdit(selectedCustomer)}
              variant="contained"
              startIcon={<Edit />}
            >
              Edit Customer
            </Button>
          )}
          {canWriteCustomer && (
          <Button
            onClick={() => handleAddLead(selectedCustomer)}
            variant="contained"
            color="success"
            startIcon={<AddCircle />}
          >
            Add Lead
          </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Status Confirmation Dialog */}
      <ConfirmModal
        open={statusConfirmDialog.open}
        onClose={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        title="Confirm Status Change"
        cancelText="Cancel"
        confirmText={statusConfirmDialog.newStatus === 'Active' ? 'Activate' : 'Deactivate'}
        type={statusConfirmDialog.newStatus === 'Active' ? 'success' : 'error'}
      >
        <Typography variant="body1">
          Are you sure you want to change the status of{' '}
          <strong>{statusConfirmDialog.customer?.name}</strong> from{' '}
          <strong>{statusConfirmDialog.customer?.status}</strong> to{' '}
          <strong>{statusConfirmDialog.newStatus}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action will {statusConfirmDialog.newStatus === 'Active' ? 'activate' : 'deactivate'} the customer account.
        </Typography>
      </ConfirmModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmModal
        open={deleteConfirmDialog.open}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete Customer"
        cancelText="Cancel"
        confirmText="Delete"
        type="error"
      >
        <Typography variant="body1">
          Are you sure you want to delete customer <strong>{deleteConfirmDialog.customer?.name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This action will mark the customer as deleted and they will no longer appear in active lists.
        </Typography>
      </ConfirmModal>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default Customer;
